import type { ICookieConsentWindow, WcpTheme } from "./CookieConsentTypes";

const requiredConsentCategoryTokens = [
  "required",
  "necessary",
  "essential",
  "strictlynecessary",
  "strictly-necessary",
] as const;

export function hasAnyGrantedConsent(consentState?: Record<string, boolean>): boolean {
  if (!consentState) {
    return false;
  }

  const optionalConsentEntries = Object.entries(consentState).filter(([category]) => {
    const normalizedCategory = category.toLowerCase();
    return !requiredConsentCategoryTokens.some((token) => normalizedCategory.includes(token));
  });

  return optionalConsentEntries.some(([, isGranted]) => Boolean(isGranted));
}

export function isConsentReset(
  previousState: boolean | null,
  currentState: Record<string, boolean>
): boolean {
  return previousState === true && !hasAnyGrantedConsent(currentState);
}

export function toConsentLocale(locale: string): string {
  const [language, region] = locale.split("-");

  if (!language || !region) {
    return locale;
  }

  return `${language.toLowerCase()}-${region.toUpperCase()}`;
}

export function getCurrentWcpTheme(): WcpTheme {
  if (window.matchMedia("(forced-colors: active)").matches) {
    return "high-contrast";
  }

  if (document.documentElement.classList.contains("theme-dark")) {
    return "dark";
  }

  return "light";
}

export function emitThemeChanged(): void {
  const currentTheme = getCurrentWcpTheme();
  const windowObj = window as ICookieConsentWindow;
  windowObj.mciCookieSiteConsent?.consentControl?.applyTheme(currentTheme);
  window.dispatchEvent(
    new CustomEvent("theme-changed", {
      detail: {
        currentTheme,
      },
    })
  );
}

export function addMediaQueryChangeListener(
  mediaQuery: MediaQueryList,
  listener: () => void
): () => void {
  if ("addEventListener" in mediaQuery) {
    mediaQuery.addEventListener("change", listener);
    return (): void => mediaQuery.removeEventListener("change", listener);
  }

  const legacyMediaQuery = mediaQuery as MediaQueryList & {
    addListener: (callback: () => void) => void;
    removeListener: (callback: () => void) => void;
  };

  legacyMediaQuery.addListener(listener);
  return (): void => legacyMediaQuery.removeListener(listener);
}
